import React, { useState } from 'react';
import { getSupabase } from '../lib/supabase.js';

// D-15 (owner design 2026-08-22): THE cost entry point — one form, both
// roles, both breakpoints. One-time and recurring are the same concept; a
// recurring entry creates a SCHEDULE (a suggestion template that never books
// by itself — its occurrences fire as red confirm cards on the bill day).
//
// Trust matrix (COST_MODULE_REDESIGN_PLAN.md §2):
//   admin one-time            → books confirmed immediately
//   investor one-time         → pending, Kareem confirms (D-14)
//   admin recurring           → schedule active, fires from its next bill day
//   investor recurring        → schedule pending Kareem's one-time approval
// The investor variant books per individual household member (self or any
// member they manage — owner ruling); paid-by is locked to "my money".

const BD = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const PANEL = { background: 'var(--surface,#fff)', border: '1px solid var(--border,#e2e8f0)', borderRadius: 14, padding: '18px 20px', width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 18px 50px rgba(2,6,23,0.35)' };
const LBL = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--text-3,#94a3b8)' };
const INP = { font: 'inherit', padding: '8px 10px', border: '1px solid var(--border-2,#d4d8e6)', borderRadius: 8, background: 'var(--surface,#fff)', color: 'var(--text-1,#0f172a)' };

export default function LogCostModal({
  mode,          // 'admin' | 'investor'
  members,       // investor: own family [{id,name,role}] · admin: flat [{id,name,household}] across households
  onClose,
  onSaved,
}) {
  const now = new Date();
  const [kind, setKind] = useState('one-time');       // 'one-time' | 'recurring'
  const [memberId, setMemberId] = useState(members?.[0]?.id || '');
  const [paidBy, setPaidBy] = useState('investor');   // admin only; investor locked
  const [date, setDate] = useState(now.toISOString().slice(0, 10));   // T-064: the day it happened; the month follows
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [name, setName] = useState('');
  const [billDay, setBillDay] = useState(String(Math.min(now.getUTCDate(), 28)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const isAdmin = mode === 'admin';

  const submit = async () => {
    const amt = Number(amount);
    if (!memberId) { setError('Pick who this cost applies to.'); return; }
    if (!Number.isFinite(amt) || amt <= 0) { setError('Enter a positive amount.'); return; }
    if (kind === 'one-time' && !note.trim()) { setError('A note is required — what was it, and for whom.'); return; }
    if (kind === 'recurring') {
      if (!name.trim()) { setError('Name the subscription (e.g. "Coinbase One").'); return; }
      const bd = Number(billDay);
      if (!Number.isInteger(bd) || bd < 1 || bd > 31) { setError('Bill day must be 1–31.'); return; }
    }
    setSaving(true); setError(null);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Supabase client unavailable');
      if (kind === 'one-time') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError('Pick the date it happened.'); setSaving(false); return; }
        const { data, error: err } = await sb.rpc('v2_record_cost_item', {
          p_member_id: memberId,
          p_month: date.slice(0, 7) + '-01',
          p_occurred_on: date,
          p_amount: amt,
          p_note: note.trim(),
          p_paid_by: isAdmin ? paidBy : 'investor',
          p_source: isAdmin ? 'admin' : 'app',
        });
        if (err) throw err;
        setDone({ kind: 'one-time', ...data });
      } else {
        const { data, error: err } = await sb.rpc('v2_upsert_cost_schedule', {
          p_member_id: memberId,
          p_name: name.trim(),
          p_amount: amt,
          p_bill_day: Number(billDay),
          p_paid_by: isAdmin ? paidBy : 'investor',
          p_note: note.trim() || null,
        });
        if (err) throw err;
        setDone({ kind: 'recurring', ...data });
      }
    } catch (e) { setError(e?.message || String(e)); }
    finally { setSaving(false); }
  };

  const toggleBtn = (val, label) => (
    <button type="button" onClick={() => setKind(val)} disabled={saving}
      style={{
        flex: 1, padding: '9px 10px', borderRadius: 9, cursor: 'pointer', font: 'inherit', fontWeight: 800, fontSize: 13,
        border: '1px solid ' + (kind === val ? 'var(--accent,#4f46e5)' : 'var(--border-2,#d4d8e6)'),
        background: kind === val ? 'var(--accent-bg,#eef2ff)' : 'var(--surface,#fff)',
        color: kind === val ? 'var(--accent-2,#4338ca)' : 'var(--text-2,#475569)',
      }}>{label}</button>
  );

  return (
    <div onClick={onClose} style={BD}>
      <div onClick={(e) => e.stopPropagation()} style={PANEL} role="dialog" aria-label="Log a cost">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--accent,#4f46e5)' }}>Costs</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, margin: '2px 0 0' }}>Log a cost</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-3,#94a3b8)', lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {done ? (
          <div>
            <div style={{
              background: (done.status === 'pending' || done.status === 'pending_approval') ? 'var(--amber-bg,#fffbeb)' : 'var(--green-bg,#f0fdf4)',
              border: '1px solid ' + ((done.status === 'pending' || done.status === 'pending_approval') ? 'var(--amber-border,#fde68a)' : 'var(--green-border,#bbf7d0)'),
              borderRadius: 10, padding: '12px 14px', fontSize: 13.5, lineHeight: 1.55,
            }}>
              {done.kind === 'one-time' && done.status === 'confirmed' && <>Recorded — <strong>${Number(done.amount || 0).toLocaleString()}</strong> is on the books.</>}
              {done.kind === 'one-time' && done.status === 'pending' && <>Sent to Kareem to confirm. <strong>${Number(done.amount || 0).toLocaleString()}</strong> counts only after he confirms it.</>}
              {done.kind === 'recurring' && done.status === 'active' && <>Schedule is live. On day {billDay} each month a <strong>red confirm card</strong> will ask whether the charge actually happened — nothing books until it's confirmed.</>}
              {done.kind === 'recurring' && done.status === 'pending_approval' && <>Sent to Kareem for a one-time approval. Once he approves, the monthly confirm card starts on day {billDay} — and each month's charge still books only when confirmed.</>}
              {done.postedAsAdjustment ? <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--text-2,#475569)' }}>That month is closed, so it posted into the open month as a dated adjustment.</div> : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" className="btn-primary" onClick={onSaved} style={{ padding: '9px 16px' }}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {toggleBtn('one-time', 'One-time')}
              {toggleBtn('recurring', 'Recurring · monthly')}
            </div>
            {error && <div style={{ background: 'var(--rose-bg,#fef2f2)', border: '1px solid var(--rose-border,#fecaca)', color: 'var(--rose,#e11d48)', padding: '9px 11px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={LBL}>Applies to
                <select value={memberId} onChange={(e) => setMemberId(e.target.value)} disabled={saving} style={INP}>
                  {(members || []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.household ? `${m.household} · ${m.name}` : m.name}{m.role === 'holder' ? ' (you)' : ''}
                    </option>
                  ))}
                </select>
              </label>

              {isAdmin ? (
                <label style={LBL}>Who paid / pays
                  <select value={paidBy} onChange={(e) => setPaidBy(e.target.value)} disabled={saving} style={INP}>
                    <option value="investor">Investor</option>
                    <option value="kareem">Kareem</option>
                  </select>
                </label>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-3,#94a3b8)' }}>Paid with <strong>your own money</strong> — if Kareem covered it, he records it from his side.</div>
              )}

              {kind === 'one-time' ? (
                <>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <label style={{ ...LBL, flex: 1 }}>Date
                      <input type="date" value={date} max={now.toISOString().slice(0, 10)} disabled={saving} onChange={(e) => setDate(e.target.value)} style={INP} className="log-cost-date" />
                    </label>
                    <label style={{ ...LBL, flex: 1 }}>Amount $
                      <input type="number" min="0.01" step="0.01" value={amount} disabled={saving} onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.currentTarget.blur()} style={INP} />
                    </label>
                  </div>
                  <label style={LBL}>What was it? (required)
                    <input type="text" value={note} disabled={saving} placeholder="e.g. replacement phone for the Coinbase login" onChange={(e) => setNote(e.target.value)} style={INP} />
                  </label>
                </>
              ) : (
                <>
                  <label style={LBL}>Subscription name (required)
                    <input type="text" value={name} disabled={saving} placeholder='e.g. "Coinbase One"' onChange={(e) => setName(e.target.value)} style={INP} />
                  </label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <label style={{ ...LBL, flex: 1 }}>Amount $ / month
                      <input type="number" min="0.01" step="0.01" value={amount} disabled={saving} onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.currentTarget.blur()} style={INP} />
                    </label>
                    <label style={{ ...LBL, flex: 1 }}>Bill day (1–31 · short months bill their last day)
                      <input type="number" min="1" max="31" step="1" value={billDay} disabled={saving} onChange={(e) => setBillDay(e.target.value)} onWheel={(e) => e.currentTarget.blur()} style={INP} />
                    </label>
                  </div>
                  <label style={LBL}>Note (optional)
                    <input type="text" value={note} disabled={saving} onChange={(e) => setNote(e.target.value)} style={INP} />
                  </label>
                  <div style={{ fontSize: 12, color: 'var(--text-2,#475569)', background: 'var(--surface-2,#f8fafc)', border: '1px dashed var(--border-2,#d4d8e6)', borderRadius: 8, padding: '8px 11px', lineHeight: 1.5 }}>
                    Recurring never books by itself. Each month a <strong style={{ color: 'var(--rose,#e11d48)' }}>red confirm card</strong> asks
                    whether the charge actually happened — confirm books it, "didn't happen" records the skip.
                  </div>
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-ghost" onClick={onClose} disabled={saving} style={{ padding: '9px 16px' }}>Cancel</button>
              <button type="button" className="btn-primary" onClick={submit} disabled={saving} style={{ padding: '9px 16px', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : (isAdmin
                  ? (kind === 'one-time' ? 'Record cost' : 'Start schedule')
                  : (kind === 'one-time' ? 'Send to Kareem' : 'Request schedule'))}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
